import utagUtils from 'utils/utag'
import { SyntheticEvent } from 'react'
import {
  OptionType,
  SelectedType
} from 'syf-component-library/ui/atoms/Select/typings'
import { ComponentInterface } from 'typings'
import Icon from 'ui/atoms/Icon'
import {
  BodyStyledAsError,
  StyledH2,
  StyledQuestionText,
  StyledSelect,
  StyledSelectContainer,
  StyledTextfield,
  Subtext
} from 'pages/InfoRequestPage.styles'
import { getRightsResetForState, placeholderFunction } from 'helpers'
import useCustomTranslation from 'hooks/useCustomTranslation'
import { currentLanguage, Languages } from 'helpers/language'
import { Grid, GridHeading } from './RequestPage.styles'

const MailingAddress = ({
  formValues,
  handleOnChange,
  handleOnBlur
}: ComponentInterface): JSX.Element => {
  const t = useCustomTranslation('form')

  const states = [
    {
      value: 'AP',
      label: t('mailing.stateOptions.AP')
    },
    {
      value: 'CA',
      label: t('mailing.stateOptions.CA')
    },
    {
      value: 'WA',
      label: t('mailing.stateOptions.WA')
    }
  ] as SelectedType[]
  const notFromStateWhitelistMessage = t('errors.notFromStateWhitelist')
  const notFromStateWhitelistURL = (
    <a
      href={`https://www.synchrony.com/privacy-policy${
        currentLanguage === Languages.Spanish ? '-sp' : ''
      }.html`}
      target="_blank"
      rel="noopener noreferrer"
      className="ensightencustomevent"
      data-type="link"
      data-reason="privacypolicy"
      data-object="ourprivacypolicy"
    >
      {t('errors.privacy')}
    </a>
  )
  const notFromStateWhitelist = (
    <BodyStyledAsError>
      {notFromStateWhitelistMessage}
      {notFromStateWhitelistURL}
    </BodyStyledAsError>
  )

  const isNotFromStateWhitelist = formValues.fromStateWhitelist?.value === 'No'
  const hideFields =
    formValues.fromStateWhitelist?.value === 'No' ||
    formValues.fromStateWhitelist?.value === ''

  return (
    <>
      <GridHeading>
        <StyledH2 style={{ maxWidth: '500px', width: '80vw' }}>
          {t('mailing.title')}
        </StyledH2>
      </GridHeading>
      <StyledQuestionText>{t('mailing.whitelisted')}</StyledQuestionText>
      <Grid>
        <StyledSelectContainer>
          <StyledSelect
            name="fromStateWhitelist"
            id="fromStateWhitelist"
            placeholder={t('optionPlaceholder') || ''}
            error={formValues.errors.fromStateWhitelist}
            options={[
              { label: t('yes'), value: 'Yes' },
              { label: t('no'), value: 'No' }
            ]}
            defaultOption={formValues.fromStateWhitelist}
            onBlur={() =>
              !formValues.fromStateWhitelist &&
              handleOnBlur('fromStateWhitelist')
            }
            onChange={value => {
              handleOnChange('fromStateWhitelist', value)
              // update view to track events
              utagUtils.view()
            }}
            rightIconOpen={Icon('up')}
            rightIconClosed={Icon('down')}
            data-testid="select-california"
          />
        </StyledSelectContainer>
      </Grid>
      <Subtext>{t('mailing.subtext')}</Subtext>
      {isNotFromStateWhitelist && notFromStateWhitelist}
      {!hideFields && (
        <Grid>
          <StyledTextfield
            type="text"
            inputMode="text"
            id="address"
            name="address"
            placeholder={t('mailing.address')}
            aria-label="Address"
            isDisabled={isNotFromStateWhitelist}
            error={formValues.errors.address}
            value={formValues.address}
            onChange={(e: SyntheticEvent<HTMLInputElement, Event>) =>
              handleOnChange('address', (e.target as HTMLInputElement).value)
            }
            onBlur={() => !formValues.address && handleOnBlur('address')}
            autoComplete="address-line1"
            maxLength={50}
            errorIcon={Icon('error')}
            data-testid="address"
          />
          <StyledTextfield
            type="text"
            inputMode="text"
            id="secondaryAddress"
            name="secondaryAddress"
            placeholder={t('mailing.secondaryAddress')}
            aria-label="Secondary Address"
            isDisabled={isNotFromStateWhitelist}
            error={formValues.errors.secondaryAddress}
            value={formValues.secondaryAddress}
            onChange={(e: SyntheticEvent<HTMLInputElement, Event>) =>
              handleOnChange(
                'secondaryAddress',
                (e.target as HTMLInputElement).value
              )
            }
            autoComplete="address-line2"
            maxLength={50}
            errorIcon={Icon('error')}
            data-testid="sec-add"
          />
          <StyledTextfield
            type="text"
            inputMode="text"
            id="city"
            name="city"
            placeholder={t('mailing.city')}
            aria-label="City"
            isDisabled={isNotFromStateWhitelist}
            error={formValues.errors.city}
            value={formValues.city}
            onChange={(e: SyntheticEvent<HTMLInputElement, Event>) =>
              handleOnChange('city', (e.target as HTMLInputElement).value)
            }
            onBlur={() => !formValues.city && handleOnBlur('city')}
            autoComplete="address-level2"
            maxLength={50}
            errorIcon={Icon('error')}
            data-testid="city"
          />
          <StyledSelectContainer>
            <StyledSelect
              id="state"
              name="state"
              placeholder={t('mailing.state') || ''}
              aria-label="State"
              isDisabled={isNotFromStateWhitelist}
              options={states}
              error={formValues.errors.state}
              defaultOption={formValues.state}
              onChange={(value: OptionType) =>
                handleOnChange(
                  'state',
                  value,
                  getRightsResetForState(value.value)
                )
              }
              onBlur={() => !formValues.state && handleOnBlur('state')}
              rightIconOpen={Icon('up')}
              rightIconClosed={Icon('down')}
              errorIcon={Icon('error')}
              data-testid="state"
            />
          </StyledSelectContainer>
          <StyledTextfield
            type="text"
            inputMode="numeric"
            id="zipCode"
            name="zipCode"
            placeholder={t('mailing.zipCode')}
            aria-label="Zip Code"
            isDisabled={isNotFromStateWhitelist}
            error={formValues.errors.zipCode}
            value={formValues.zipCode}
            maxLength={5}
            matchPattern={/^\d*$/}
            onChange={(e: SyntheticEvent<HTMLInputElement, Event>) => {
              handleOnChange('zipCode', (e.target as HTMLInputElement).value)
            }}
            onBlur={() => !formValues.zipCode && handleOnBlur('zipCode')}
            autoComplete="postal-code"
            errorIcon={Icon('error')}
            data-testid="zip-code"
          />
          <StyledTextfield
            type="text"
            inputMode="text"
            id="country"
            name="country"
            placeholder={t('mailing.country')}
            aria-label="Country"
            value={formValues.country.label}
            isDisabled
            autoComplete="country"
            data-testid="country"
            onChange={placeholderFunction}
          />
        </Grid>
      )}
    </>
  )
}

export default MailingAddress
